import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { FileText, Plus, Download, Loader2, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";

const TestsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token, refreshUser, user } = useAuth();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewTest, setViewTest] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [deleteTest, setDeleteTest] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const response = await axios.get(`${API}/tests`, {
        headers: getAuthHeaders(token)
      });
      setTests(response.data);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (test) => {
    if ((user?.credits || 0) < 2) {
      toast.error(t('errors.insufficientCredits'));
      navigate("/pricing");
      return;
    }

    setDownloading(test.test_id);
    try {
      const response = await axios.post(
        `${API}/tests/${test.test_id}/download`,
        {},
        { 
          headers: getAuthHeaders(token),
          responseType: 'blob'
        }
      );
      
      await refreshUser();
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `test-${test.test_id}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success("Test PDF downloaded!");
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error("Insufficient credits");
        navigate("/pricing");
      } else {
        toast.error("Failed to download");
      }
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTest) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/tests/${deleteTest.test_id}`, {
        headers: getAuthHeaders(token)
      });
      toast.success("Test deleted successfully");
      setTests(tests.filter(t => t.test_id !== deleteTest.test_id));
      setDeleteTest(null);
    } catch (error) {
      toast.error("Failed to delete test");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tests-page">
      <DashboardHeader />

      <main className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                My Tests
              </h1>
              <p className="text-slate-600">View and download your generated tests</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate("/books")}
            className="btn-primary gap-2"
            data-testid="create-test-btn"
          >
            <Plus className="w-4 h-4" />
            Create Test
          </Button>
        </div>

        {tests.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tests.map((test) => (
              <Card 
                key={test.test_id}
                className="card-base card-interactive group"
                data-testid={`test-card-${test.test_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-teal-50 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#14B8A6]" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                        {test.questions?.length || 0} questions
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTest(test);
                        }}
                        data-testid={`delete-test-${test.test_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{test.title}</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Difficulty: {test.config?.difficulty || 50}% • {new Date(test.created_at).toLocaleDateString()}
                  </p>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setViewTest(test)}
                      data-testid={`view-test-${test.test_id}`}
                    >
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 btn-primary"
                      onClick={() => handleDownload(test)}
                      disabled={downloading === test.test_id}
                      data-testid={`download-test-${test.test_id}`}
                    >
                      {downloading === test.test_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card-base">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 mx-auto bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">No tests yet</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                Upload a book and generate your first test
              </p>
              <Button 
                onClick={() => navigate("/books")}
                className="btn-primary"
                data-testid="create-first-test-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Test
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Test Preview Dialog - Non-selectable text */}
      <Dialog open={!!viewTest} onOpenChange={() => setViewTest(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{viewTest?.title}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div 
              className="space-y-4 pr-4"
              style={{ 
                userSelect: 'none', 
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            >
              {viewTest?.questions?.map((q, i) => (
                <div key={q.question_id} className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#4F46E5] text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 mb-2">{q.question}</p>
                      {q.type === 'multiple_choice' && q.options && (
                        <div className="space-y-1">
                          {q.options.map((opt, j) => (
                            <p key={j} className="text-sm text-slate-600 pl-4">
                              {String.fromCharCode(65 + j)}) {opt}
                            </p>
                          ))}
                        </div>
                      )}
                      {q.type === 'true_false' && (
                        <div className="space-y-1">
                          <p className="text-sm text-slate-600 pl-4">A) True</p>
                          <p className="text-sm text-slate-600 pl-4">B) False</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setViewTest(null)}>
              Close
            </Button>
            <Button 
              className="btn-primary"
              onClick={() => {
                handleDownload(viewTest);
                setViewTest(null);
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download (2 credits)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTest} onOpenChange={() => setDeleteTest(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Test?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTest?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TestsPage;
